<template>
  <div class="about-page">
    <!-- Hero -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-header">
          <p class="eyebrow">GLOBAL AI LINK LIMITED</p>
          <h1>About Us</h1>
        </div>

        <div class="hero-body">
          <div class="about-intro">
            <p class="intro-lead">
              <strong>GLOBAL AI LINK LIMITED</strong> is a professional technology
              enterprise headquartered in Hong Kong, officially incorporated in
              2025. Centered on cross-border digital advertising and mobile
              internet technology, we integrate independent research, development
              and technical service output to serve global cross-border
              operators, app developers and overseas brand merchants.
            </p>
            <div class="intro-highlight">
              <div class="highlight-icon">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2L14.4 8.4L21 9.6L15.6 13.8L17.4 20.4L12 16.8L6.6 20.4L8.4 13.8L3 9.6L9.6 8.4L12 2Z"
                    fill="#4b89ff"
                  />
                </svg>
              </div>
              <p>
                Backed by a technical team with rich overseas advertising
                compliance experience, we deliver stable, compliant and
                data-driven technical products and one-stop operational support
                for worldwide cross-border businesses.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Mission -->
    <section class="split-section split-section--white">
      <div class="container split-container">
        <div class="split-image">
          <div class="split-image-frame">
            <img
              src="../../static/images/globalai002.png"
              alt="Our mission illustration"
            />
          </div>
        </div>
        <div class="split-content">
          <span class="section-tag">Our Mission</span>
          <h2>Leverage AI for Global Growth</h2>
          <p>
            We aim to leverage cutting-edge artificial intelligence technologies
            to resolve core pain points encountered by global overseas brands,
            including inefficient global traffic acquisition, complicated App
            compliance publishing procedures, and inaccurate cross-platform ad
            data tracking. We integrate self-developed advertising systems,
            custom mobile app development and compliance consulting capabilities
            into a complete ecosystem, delivering reliable, scalable one-stop
            technical development and operation support services that help
            cross-border enterprises cut operating costs, improve advertising
            conversion rates and achieve sustainable overseas business growth.
          </p>
        </div>
      </div>
    </section>

    <!-- Team -->
    <section class="split-section split-section--alt">
      <div class="container split-container split-container--reverse">
        <div class="split-image">
          <div class="split-image-frame">
            <img
              src="../../static/images/globalai003.png"
              alt="Our team illustration"
            />
          </div>
        </div>
        <div class="split-content">
          <span class="section-tag">Our Team</span>
          <h2>Experienced Cross-border Specialists</h2>
          <p>
            Our team consists of seasoned global media buying specialists,
            professional native iOS & Android developers, and senior backend
            system engineers with years of hands-on experience operating Meta,
            Voluum and various ad attribution tracking systems. Our
            cross-functional technical and operation team has in-depth insights
            into cross-border advertising rules, mobile application compliance
            standards and full-link traffic data analysis. We independently
            develop a full suite of automated advertising tools, intelligent
            campaign optimization platforms and self-hosted tracking dashboards
            tailored for overseas marketers. Every member brings solid industry
            expertise to deliver stable, compliant and high-efficiency technical
            solutions to our worldwide clients.
          </p>
        </div>
      </div>
    </section>

    <!-- Advantages -->
    <section class="advantages-section">
      <div class="container">
        <div class="section-head">
          <span class="section-tag section-tag--center">Why Choose Us</span>
          <h2>Company Advantages</h2>
          <p>
            End-to-end capabilities across technology, media buying, and
            compliance — built for scalable global advertising operations.
          </p>
        </div>
        <div class="advantages-grid">
          <article
            v-for="item in advantages"
            :key="item.title"
            class="advantage-card"
          >
            <div class="advantage-icon-box">
              <img :src="item.icon" :alt="item.title" />
            </div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "AboutUsPage",
  data() {
    return {
      advantages: [
        {
          title: "Self-developed Ad Systems",
          desc: "Self-developed ad tracking and AI optimization systems with independent technical intellectual property.",
          icon: require("../../static/images/publisher/feature-icon-1.png"),
        },
        {
          title: "App Store Expertise",
          desc: "Professional experience with Apple Developer Program and full App Store workflow.",
          icon: require("../../static/images/publisher/feature-icon-2.png"),
        },
        {
          title: "Search Arbitrage Excellence",
          desc: "Leveraging advanced data analysis and RSOC/AFS strategies to optimize search traffic and generate higher RPM.",
          icon: require("../../static/images/publisher/feature-icon-3.png"),
        },
        {
          title: "AI-Driven Optimization",
          desc: "Applying cutting-edge AI algorithms to enhance targeting, bidding, and conversion tracking.",
          icon: require("../../static/images/publisher/feature-icon-4.png"),
        },
        {
          title: "Professional Media-buy Team",
          desc: "A large-scale and professional media-buy team with strong expertise in creative, data analytics, and optimization across search arbitrage, lead generation and performance advertising.",
          icon: require("../../static/images/advertiser/feature-icon-1.png"),
        },
        {
          title: "Compliance & Sustainability",
          desc: "Ensuring long-term compliant advertising practices with AI-powered systems and manual review to safeguard partner accounts and revenues.",
          icon: require("../../static/images/advertiser/feature-icon-2.png"),
        },
        {
          title: "Big Scale & Fast Growth",
          desc: "Under full creative compliance and platform policies, our media buying team can rapidly scale advertising spend, boosting both volume and efficiency.",
          icon: require("../../static/images/advertiser/feature-icon-3.png"),
        },
      ],
    };
  },
};
</script>

<style scoped>
.about-page {
  font-family: "Poppins", sans-serif;
  color: #1c2b4e;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero */
.hero-section {
  position: relative;
  padding: 150px 0 88px;
  background-image: url("../../static/images/publisher/hero-bg.jpg");
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-color: #f0f7ff;
  overflow: hidden;
}

.hero-section::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(240, 247, 255, 0.55) 0%,
    rgba(255, 255, 255, 0.88) 100%
  );
  pointer-events: none;
}

.hero-header {
  position: relative;
  z-index: 1;
  max-width: 820px;
  margin: 0 auto 40px;
  text-align: center;
}

.eyebrow {
  margin: 0 0 16px;
  font-size: 13px;
  letter-spacing: 1.8px;
  color: #4b89ff;
  font-weight: 700;
}

.hero-header h1 {
  margin: 0 0 8px;
  font-size: 48px;
  line-height: 1.15;
  font-weight: 700;
  color: #1c2b4e;
  letter-spacing: -0.3px;
}

.hero-header h1::after {
  content: "";
  display: block;
  width: 64px;
  height: 4px;
  margin: 20px auto 0;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  border-radius: 2px;
}

.hero-body {
  position: relative;
  z-index: 1;
}

.about-intro {
  max-width: 860px;
  margin: 0 auto;
  text-align: center;
}

.intro-lead {
  font-size: 18px;
  line-height: 1.85;
  color: #334155;
  margin: 0 auto 48px;
}

.intro-lead strong {
  color: #1c2b4e;
  font-weight: 700;
}

.intro-highlight {
  position: relative;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(75, 137, 255, 0.15);
  border-radius: 24px;
  padding: 40px 48px;
  box-shadow: 0 20px 48px rgba(28, 43, 78, 0.05);
  backdrop-filter: blur(10px);
}

.highlight-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(75, 137, 255, 0.08);
  margin-bottom: 24px;
}

.intro-highlight p {
  margin: 0;
  font-size: 17px;
  line-height: 1.8;
  color: #1c2b4e;
  font-weight: 500;
}

/* Split sections */
.split-section {
  padding: 88px 0;
}

.split-section--white {
  background: #ffffff;
}

.split-section--alt {
  background: linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%);
}

.split-container {
  display: flex;
  align-items: center;
  gap: 56px;
}

.split-container--reverse {
  flex-direction: row-reverse;
}

.split-image {
  flex: 0 0 44%;
}

.split-image-frame {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(28, 43, 78, 0.08);
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
}

.split-image-frame::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 1px solid rgba(75, 137, 255, 0.12);
  border-radius: 24px;
  pointer-events: none;
}

.split-image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 24px;
}

.split-content {
  flex: 1;
}

.section-tag {
  display: inline-block;
  margin-bottom: 14px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #4b89ff;
  background: rgba(75, 137, 255, 0.1);
  border-radius: 999px;
}

.section-tag--center {
  display: table;
  margin-left: auto;
  margin-right: auto;
}

.split-content h2 {
  margin: 0 0 20px;
  font-size: 38px;
  line-height: 1.25;
  font-weight: 700;
  color: #1c2b4e;
  letter-spacing: -0.5px;
}

.split-content p {
  margin: 0;
  font-size: 16px;
  line-height: 1.85;
  color: #475569;
}

/* Advantages */
.advantages-section {
  padding: 88px 0 100px;
  background: #f0f8ff;
}

.section-head {
  text-align: center;
  max-width: 680px;
  margin: 0 auto 52px;
}

.section-head h2 {
  margin: 0 0 16px;
  font-size: 42px;
  line-height: 1.2;
  font-weight: 700;
  color: #1c2b4e;
  letter-spacing: -0.5px;
}

.section-head h2::after {
  content: "";
  display: block;
  width: 72px;
  height: 4px;
  margin: 18px auto 0;
  background: linear-gradient(90deg, #4b89ff, #296dff);
  border-radius: 2px;
}

.section-head p {
  margin: 22px 0 0;
  font-size: 17px;
  line-height: 1.75;
  color: #64748b;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
}

.advantage-card {
  background: #ffffff;
  border: 1px solid rgba(75, 137, 255, 0.08);
  border-radius: 20px;
  padding: 32px 28px;
  box-shadow: 0 10px 32px rgba(28, 43, 78, 0.04);
  text-align: left;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
}

.advantage-card:hover {
  transform: translateY(-6px);
  border-color: rgba(75, 137, 255, 0.2);
  box-shadow: 0 18px 48px rgba(75, 137, 255, 0.1);
}

.advantage-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(75, 137, 255, 0.12) 0%, rgba(75, 137, 255, 0.02) 100%);
  border-radius: 16px;
}

.advantage-card img {
  display: block;
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.advantage-card h3 {
  margin: 0 0 12px;
  font-size: 19px;
  line-height: 1.35;
  font-weight: 700;
  color: #1c2b4e;
}

.advantage-card p {
  margin: 0;
  font-size: 15px;
  line-height: 1.75;
  color: #64748b;
}

/* Last card spans full width on odd count — optional center for 7th */
.advantage-card:last-child:nth-child(3n + 1) {
  grid-column: 1 / -1;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
}

@media (max-width: 992px) {
  .hero-header h1 {
    font-size: 40px;
  }

  .intro-highlight {
    padding: 32px 28px;
    border-radius: 20px;
  }

  .split-container,
  .split-container--reverse {
    flex-direction: column;
    text-align: center;
  }

  .split-image {
    flex: none;
    width: 100%;
    max-width: 420px;
    margin: 0 auto;
  }

  .split-content h2 {
    font-size: 30px;
  }

  .section-head h2 {
    font-size: 32px;
  }

  .advantages-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .advantage-card:last-child:nth-child(3n + 1) {
    grid-column: auto;
    max-width: none;
  }
}

@media (max-width: 576px) {
  .hero-section {
    padding-top: 120px;
    padding-bottom: 64px;
  }

  .hero-header h1 {
    font-size: 32px;
  }

  .intro-lead {
    font-size: 16px;
    margin-bottom: 32px;
  }

  .intro-highlight {
    padding: 28px 24px;
  }

  .intro-highlight p {
    font-size: 15px;
  }

  .split-section,
  .advantages-section {
    padding: 64px 0;
  }

  .advantages-grid {
    grid-template-columns: 1fr;
  }

  .advantage-card {
    padding: 22px 20px;
  }
}
</style>
